import { useGetInterviewDetailsQuery } from "@/app/features/recruitment/evaluation/evaluation.api";
import type { InterviewQuestion } from "@/app/features/recruitment/evaluation/evaluation.type";
import CustomDrawer from "@/components/CustomDrawer";
import type { CustomModalProps } from "@/components/CustomModal";
import WithLoader from "@/components/WithLoader";
import { cn } from "@/lib/cn";
import { formatDate } from "@/utils";
import { Divider, Flex } from "antd";

type Props = CustomModalProps & {
  id?: string;
  applicantName?: string;
  interviewType?: "ai" | "manual";
};

interface Step {
  label: string;
  value: string;
  status: "completed" | "pending" | "processing" | "ready";
}

interface InterviewStatusCardProps {
  stageTitle: string;
  dotColorClass: string;
  steps: Step[];
}

const InterviewStatusCard = ({
  stageTitle,
  dotColorClass,
  steps,
}: InterviewStatusCardProps) => {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8 flex flex-col md:flex-row gap-6 items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className={cn("size-4 rounded-full", dotColorClass)} />
        </div>
        <div>
          <h6 className="text-white/40 text-[11px] uppercase tracking-[0.2em] font-black mb-1">
            Current Stage
          </h6>
          <h4 className="text-xl font-bold text-white">{stageTitle}</h4>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {steps.map((step, index) => (
          <div key={step.label} className="flex items-center gap-3">
            {index > 0 && <div className="w-6 h-px bg-white/10" />}
            <div
              className={cn(
                "flex flex-col items-center gap-1.5 px-4 py-2 rounded-xl border transition-all duration-300",
                step.status === "completed" || step.status === "ready"
                  ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
                  : step.status === "processing"
                    ? "border-theme-primary/30 bg-theme-primary/5 text-theme-primary animate-pulse"
                    : "border-white/5 bg-white/[0.02] text-white/20",
              )}
            >
              <span className="text-[10px] font-black uppercase tracking-wider">
                {step.label}
              </span>
              <span className="text-xs font-bold">{step.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ViewEvaluatedMarks({
  handleClose,
  open,
  id,
  applicantName,
  interviewType,
}: Props) {
  const {
    isLoading,
    isFetching,
    data: detailsData,
  } = useGetInterviewDetailsQuery(id!, {
    skip: !open || !id,
  });

  const interviewDetails = detailsData?.data;

  // Calculate scores/percentages based on interview type
  const isAi = interviewType === "ai";
  const totalObtained = (interviewDetails?.questions ?? []).reduce(
    (sum: number, q: InterviewQuestion) => {
      const score = isAi ? q.score_from_ai : q.obtained_mark;
      return sum + (Number(score) || 0);
    },
    0,
  );
  const totalFull = (interviewDetails?.questions ?? []).reduce(
    (sum: number, q: InterviewQuestion) =>
      sum + (interviewDetails?.interview_type === "manual" ? Number(q.mark) : 100),
    0,
  );
  const percentage =
    totalFull > 0 ? Math.round((totalObtained / totalFull) * 100) : 0;

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={`${interviewDetails?.job_application?.name || applicantName || "Applicant"}'s Marks' Breakdown`}
    >
      <WithLoader isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}>
        <div className="space-y-6 min-h-[400px]">
          {/* Applicant info header */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h6 className="text-white/70 text-[17px]">
                Assigned Interviewer
              </h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.evaluator_name || "N/A"}
              </h4>
            </div>
            <div>
              <h6 className="text-white/70 text-[17px]">Applicant</h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.job_application?.name || "N/A"}
              </h4>
            </div>
            <div>
              <h6 className="text-white/70 text-[17px]">Interview Date</h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.interview_date
                  ? formatDate(interviewDetails.interview_date, "DD/MM/YYYY")
                  : "N/A"}{" "}
                {interviewDetails?.interview_time && (
                  <span className="text-base font-normal text-white/60">
                    {interviewDetails.interview_time}
                  </span>
                )}
              </h4>
            </div>
          </div>

          <Divider />

          {/* Render manual or AI layout */}
          {isAi ? (
            /* AI Interview view mode */
            <div className="space-y-6">
              {/* Interview Stage Status Indicator */}
              {interviewDetails &&
                (() => {
                  const dotColor = !interviewDetails.is_submitted_ai_interview
                    ? "bg-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.5)]"
                    : !interviewDetails.is_ai_interview_processing_completed
                      ? "bg-theme-primary animate-pulse shadow-[0_0_15px_rgba(0,198,174,0.5)]"
                      : "bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]";

                  const stageTitle = !interviewDetails.is_submitted_ai_interview
                    ? "Awaiting Candidate Submission"
                    : !interviewDetails.is_ai_interview_processing_completed
                      ? "AI Processing in Progress"
                      : "AI Evaluation Completed";

                  const steps: Step[] = [
                    {
                      label: "Submission",
                      value: interviewDetails.is_submitted_ai_interview
                        ? "COMPLETED"
                        : "PENDING",
                      status: interviewDetails.is_submitted_ai_interview
                        ? "completed"
                        : "pending",
                    },
                    {
                      label: "AI Evaluation",
                      value:
                        interviewDetails.is_ai_interview_processing_completed
                          ? "READY"
                          : "PROCESSING",
                      status:
                        interviewDetails.is_ai_interview_processing_completed
                          ? "ready"
                          : "processing",
                    },
                  ];

                  return (
                    <InterviewStatusCard
                      stageTitle={stageTitle}
                      dotColorClass={dotColor}
                      steps={steps}
                    />
                  );
                })()}

              {/* AI Overall Feedback Section */}
              {interviewDetails?.overall_feedback && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Strengths */}
                    <div>
                      <h6 className="text-[#00C6AE] text-[20px] font-medium mb-4">
                        Over All Strength
                      </h6>
                      <ul className="space-y-3 list-disc pl-5">
                        {interviewDetails.overall_feedback.strengths?.map(
                          (strength: string, index: number) => (
                            <li key={index} className="text-white text-[15px]">
                              {strength}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>

                    {/* Weaknesses */}
                    <div>
                      <h6 className="text-[#00C6AE] text-[20px] font-medium mb-4">
                        Over All Weakness
                      </h6>
                      <ul className="space-y-3 list-disc pl-5">
                        {interviewDetails.overall_feedback.weaknesses?.map(
                          (weakness: string, index: number) => (
                            <li key={index} className="text-white text-[15px]">
                              {weakness}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>

                    {/* Recommendation */}
                    <div>
                      <h6 className="text-[#00C6AE] text-[20px] font-medium mb-4">
                        Recommendation
                      </h6>
                      <div
                        className={cn(
                          "inline-block px-4 py-1.5 rounded-full text-base font-semibold border",
                          interviewDetails.overall_feedback.recommendation ===
                            "Reject"
                            ? "bg-rose-500/20 text-rose-400 border-rose-500/30"
                            : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
                        )}
                      >
                        {interviewDetails.overall_feedback.recommendation}
                      </div>
                      <h6 className="text-[#00C6AE] text-[20px] font-medium mb-4 mt-4">
                        Total Score
                      </h6>
                      <span
                        className={cn(
                          "px-4 py-1.5 rounded-full text-xl font-semibold",
                          percentage >= 60
                            ? "bg-theme-primary/20 text-theme-primary"
                            : "bg-rose-500/20 text-rose-400",
                        )}
                      >
                        {percentage}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <Divider />

              {/* AI Question List */}
              <div className="space-y-0">
                {(interviewDetails?.questions ?? []).map(
                  (q: InterviewQuestion, index: number) => (
                    <div
                      key={q.id || index}
                      className="border-b border-b-white/60 pb-6 pt-6 border-dashed last:border-0 last:pb-0"
                    >
                      <Flex gap={24} justify="space-between" align="start">
                        {/* Question text */}
                        <div className="!w-full !grow">
                          <label className="text-theme-gray-300 block mb-2 text-sm uppercase tracking-wider font-bold">
                            Question {index + 1}
                          </label>
                          <span className="text-base text-white font-semibold">
                            {q.question || `Question ${index + 1}`}
                          </span>
                        </div>

                        {/* Full Mark */}
                        <div className="w-20 shrink-0">
                          <label className="text-theme-gray-300 block mb-2 text-sm tracking-wider">
                            Full Mark
                          </label>
                          <span className="text-base text-white block font-bold">
                            100
                          </span>
                        </div>

                        {/* Score From AI */}
                        <div className="w-28 shrink-0">
                          <label className="text-theme-gray-300 block mb-2 text-sm tracking-wider">
                            Score From AI
                          </label>
                          <span className="text-base text-theme-primary block font-bold">
                            {q.score_from_ai || 0}
                          </span>
                        </div>
                      </Flex>

                      <div className="mt-5 space-y-5">
                        <div>
                          <h6 className="text-[#00C6AE] text-[17px] mb-2 font-medium">
                            Applicant Answer
                          </h6>
                          <div className="p-4 rounded-lg border border-white/10 bg-white/5 text-white/80 text-base leading-relaxed min-h-[60px]">
                            {q.candidate_answer || "N/A"}
                          </div>
                        </div>

                        <div>
                          <h6 className="text-[#00C6AE] text-[17px] mb-2 font-medium">
                            Ideal Answer
                          </h6>
                          <div className="p-4 rounded-lg border border-white/10 bg-white/5 text-white/80 text-base leading-relaxed min-h-[60px]">
                            {q.answer || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          ) : (
            /* Manual Interview view mode */
            <div className="space-y-6">
              {/* Interview Stage Status Indicator */}
              {interviewDetails && (
                <InterviewStatusCard
                  stageTitle="Evaluation Completed"
                  dotColorClass="bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]"
                  steps={[
                    {
                      label: "Evaluation",
                      value: "COMPLETED",
                      status: "completed",
                    },
                  ]}
                />
              )}

              {/* Overall Feedback Section */}
              {interviewDetails && (
                <div className="flex items-center justify-end gap-3">
                  <div>
                    <h6 className="text-theme-primary text-[20px] font-medium mb-4">
                      Total Score
                    </h6>
                    <span
                      className={cn(
                        "px-4 py-1.5 rounded-full text-xl font-semibold",
                        percentage >= 60
                          ? "bg-theme-primary/20 text-theme-primary"
                          : "bg-rose-500/20 text-rose-400",
                      )}
                    >
                      {percentage}%
                    </span>
                  </div>
                </div>
              )}

              <Divider />

              {/* Manual Question List */}
              <div className="space-y-0">
                {(interviewDetails?.questions ?? []).map(
                  (q: InterviewQuestion, index: number) => (
                    <div
                      key={q.id || index}
                      className="border-b border-b-white/10 pb-8 pt-6 last:border-0 last:pb-0"
                    >
                      <Flex gap={24} justify="space-between" align="start">
                        {/* Question text */}
                        <div className="!w-full !grow">
                          <label className="text-white/60 block mb-2 text-sm uppercase tracking-wider font-bold">
                            Question {index + 1}
                          </label>
                          <span className="text-lg text-white font-semibold">
                            {q.question || `Question ${index + 1}`}
                          </span>
                        </div>

                        {/* Full Mark */}
                        <div className="w-24 shrink-0 mt-2.5">
                          <label className="text-white block mb-2 text-sm tracking-wider">
                            Full Mark
                          </label>
                          <span className="text-lg text-white block font-bold">
                            {q.mark}
                          </span>
                        </div>

                        {/* Obtained Mark */}
                        <div className="w-24 shrink-0 text-right">
                          <label className="text-white block mb-2 text-sm mt-2.5 tracking-wider">
                            Mark
                          </label>
                          <span className="text-theme-primary text-xl block font-bold">
                            {q.obtained_mark ?? "—"}
                          </span>
                        </div>
                      </Flex>

                      <div className="mt-6 space-y-6">
                        {/* Comment field */}
                        <div className="mb-0">
                          <span className="text-[15px] font-bold tracking-wide block mb-2">
                            Interviewer Comment
                          </span>
                          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.03] text-white/80 text-base italic">
                            {q.comment || "No comment provided"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </WithLoader>
    </CustomDrawer>
  );
}
